import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { debounce } from 'ts-debounce';
import toast from 'react-hot-toast';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { useCurrentCompany } from 'src/modules/companies/hooks';
import { useCreateKeywordFormDialogController } from 'src/modules/keywords/controllers';
import { useCampaignTypeSelectionDialogController } from 'src/modules/campaigns/controllers';
import { selectProfile } from 'src/modules/profile/slices';
import { useHomeService } from '../services';
import {
  defaultGranularityForPeriod,
  formatBucketLabel,
  getMetricChartData,
  granularityToBreakdown,
  loadGranularity,
  saveGranularity,
} from '../helpers';
import { TimePeriod } from '../types';
import { useChartConfig } from './useChartConfig';
import { useContactValidation } from './useContactValidation';

// Date range for a preset period. Returned as a single object so timePeriod and
// its dates can be committed in one state update (no stale-date intermediate
// render that would fire a duplicate metrics request).
const getPeriodDates = (
  period: TimePeriod,
): { startDateInterval: string | null; endDateInterval: string | null } => {
  // Inclusive range of exactly `days` calendar days ending today (e.g. 7 days -> today-6..today).
  // Use local format, not toISOString, so dates don't shift a day in UTC+ timezones.
  const end = dayjs(new Date()).startOf('day').format('YYYY-MM-DD');
  const startMinus = (days: number) =>
    dayjs(new Date()).startOf('day').subtract(days - 1, 'days').format('YYYY-MM-DD');
  switch (period) {
    case TimePeriod.LAST7DAYS:
      return { endDateInterval: end, startDateInterval: startMinus(7) };
    case TimePeriod.LAST30DAYS:
      return { endDateInterval: end, startDateInterval: startMinus(30) };
    case TimePeriod.LAST90DAYS:
      return { endDateInterval: end, startDateInterval: startMinus(90) };
    case TimePeriod.LAST365DAYS:
      return { endDateInterval: end, startDateInterval: startMinus(365) };
    case TimePeriod.CUSTOM:
    default:
      return { startDateInterval: null, endDateInterval: null };
  }
};

export const useHomeViewController = () => {
  const navigate = useNavigate();
  const { profile } = useSelector(selectProfile);
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const { company, loading: companyLoading, refetch: refetchCompany } = useCurrentCompany();

  const { loading, metrics, counters, filter, getMetrics, getCounters, changeFilter } =
    useHomeService();
  const { timePeriod, startDateInterval, endDateInterval } = filter;

  const chartConfig = useChartConfig();

  const [granularity, setGranularityState] = useState(() => loadGranularity());
  const setGranularity = (next: typeof granularity) => {
    setGranularityState(next);
    saveGranularity(next);
  };

  const { validationInProgress, handleValidateContacts } = useContactValidation(
    refetchCompany,
    () => {
      if (selectedCompanyId) {
        getMetrics(selectedCompanyId, granularityToBreakdown(granularity));
      }
    },
  );

  const createKeywordFormDialogController = useCreateKeywordFormDialogController();
  const campaignTypeSelectionDialogController = useCampaignTypeSelectionDialogController();

  const refreshKeywords = () => {
    toast.success('keyword is successfully created');
  };

  // Fetch metrics when company/date/period changes (debounced for CUSTOM). Mirrors original.
  const runGetMetrics = useCallback(() => {
    if (!selectedCompanyId || !startDateInterval || !endDateInterval) {
      return;
    }
    const startDate = dayjs(startDateInterval);
    const endDate = dayjs(endDateInterval);
    if (!startDate.isValid() || !endDate.isValid()) {
      return;
    }
    getMetrics(selectedCompanyId, granularityToBreakdown(granularity));
  }, [selectedCompanyId, startDateInterval, endDateInterval, getMetrics, granularity]);

  const runGetMetricsRef = useRef(runGetMetrics);
  runGetMetricsRef.current = runGetMetrics;

  const debouncedGetMetrics = useRef(
    debounce(() => {
      runGetMetricsRef.current();
    }, 500),
  ).current;

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    if (timePeriod === TimePeriod.CUSTOM) {
      debouncedGetMetrics();
    } else {
      runGetMetrics();
    }
  }, [selectedCompanyId, timePeriod, startDateInterval, endDateInterval, granularity, runGetMetrics, debouncedGetMetrics]);

  // Load counters when company changes.
  useEffect(() => {
    if (selectedCompanyId) {
      getCounters(selectedCompanyId);
    }
  }, [selectedCompanyId, getCounters]);

  // Populate the date range once on mount (initial state leaves endDateInterval
  // null). Subsequent preset changes flow through setTimePeriod, which commits
  // the period and its dates atomically.
  const didInitDates = useRef(false);
  useEffect(() => {
    if (didInitDates.current) {
      return;
    }
    didInitDates.current = true;
    if (timePeriod !== TimePeriod.CUSTOM && !endDateInterval) {
      changeFilter(getPeriodDates(timePeriod));
    }
  }, [timePeriod, endDateInterval, changeFilter]);

  // Set period and its date range in a single update so the metrics effect fires
  // exactly once (previously: one fire with stale dates, then another after the
  // separate date-derive effect updated them).
  // Changing the look-back window resets granularity to the sensible default for
  // that window (365d -> monthly, 90d -> weekly). User can still override after.
  const setTimePeriod = (period: TimePeriod) => {
    setGranularity(defaultGranularityForPeriod(period));
    changeFilter({ timePeriod: period, ...getPeriodDates(period) });
  };

  const setCustomRange = (start: string, end: string) =>
    changeFilter({ timePeriod: TimePeriod.CUSTOM, startDateInterval: start, endDateInterval: end });

  const customLabel =
    startDateInterval && endDateInterval
      ? `${dayjs(startDateInterval).format('MM/DD/YYYY')} - ${dayjs(endDateInterval).format('MM/DD/YYYY')}`
      : 'Select dates';

  // BE already aggregates by breakdownBy; just relabel each bucket for the axis.
  const breakdown = granularityToBreakdown(granularity);
  const chartData = useMemo(
    () =>
      getMetricChartData(metrics).map(row => ({
        ...row,
        label: formatBucketLabel(row.bucketStart, row.bucketEnd, breakdown),
      })),
    [metrics, breakdown],
  );

  const planCredits = company?.subscription?.plan?.credits;
  const creditsRemaining = company?.subscription?.creditsRemaining;
  const creditsInPercents =
    planCredits && creditsRemaining != null
      ? Math.min(100, Math.max(0, (100 * creditsRemaining) / planCredits))
      : 0;

  return {
    navigate,
    profile,
    selectedCompanyId,
    company,
    companyLoading,
    refetchCompany,
    // metrics
    metricsLoading: loading.metrics,
    chartData,
    // counters
    counters,
    countersLoading: loading.counters,
    // filter
    timePeriod,
    setTimePeriod,
    startDateInterval,
    endDateInterval,
    customLabel,
    setCustomRange,
    // granularity
    granularity,
    setGranularity,
    // chart config
    ...chartConfig,
    // validation
    validationInProgress,
    handleValidateContacts,
    // credits
    creditsInPercents,
    // dialogs
    createKeywordFormDialogController,
    campaignTypeSelectionDialogController,
    refreshKeywords,
  };
};

export type HomeViewControllerResult = ReturnType<typeof useHomeViewController>;
